import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Case from 'case';
import { Box } from 'grommet';
import { useAppTheme } from '@Components/Context';
import {
  Seo, InfoNotice,
} from '@Components/Control';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import { DomainListTablePartial } from '@Components/Partial/Product/CompanySettings';
import { pluralize } from '@Helpers/util';
import {
  initiateCompanyUnitsRequest,
  clearCompanyUnitsRequest,
  updateCompanyUnitsRequest,
} from '@Actions';


const CompanyUnitsPage = ({
  small,
  location,
  loading,
  submitLoading,
  fetchCompanyUnits,
  updateCompanyUnits,
  clearCompanyUnits,
  searchConfig,
  paymentPending,
  companyUnits = null,
}) => {
  const { theme: { page }, isDarkMode } = useAppTheme();
  React.useEffect(() => {
    fetchCompanyUnits();

    return () => {
      clearCompanyUnits();
    };
  }, []);

  const unitTerm = searchConfig?.unitTerm;
  const isLoading = Boolean(loading || submitLoading);
  const usePageBg = isDarkMode ? page.background : page.altBg;

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: `Manage ${Case.lower(pluralize(unitTerm))}`,
        subTitle: 'Settings',
      }}
    >
      <Seo />
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '1.5rem' }}
        height="100%"
        gap="1rem"
      >
        <InfoNotice
          header={`Manage your ${Case.lower(pluralize(unitTerm))}`}
          text={`${pluralize(unitTerm)} added here can be tied to searches and results, which provides the option for DMCA letters to be sent on their behalf.`}
        />
        <DomainListTablePartial
          unitsApiMode
          key="unit-management-table"
          pageTitle={`${Case.pascal(unitTerm)}s`}
          small={small}
          loading={isLoading}
          domainData={companyUnits?.units}
          updateUnitsRequest={(values, actionType) => (
            updateCompanyUnits(unitTerm, { units: values }, actionType)
          )}
          actionTerm={unitTerm}
          headerMap={[
            { uid: 'unit', display: unitTerm, size: small ? '75%' : '85%' },
            { uid: 'actions', display: 'Actions', size: small ? '25%' : '15%' },
          ]}
          paymentPending={paymentPending}
        />
      </Box>
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    loading: state.fetchLoader.dataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
    companyUnits: state.companySettings.companyUnits,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchCompanyUnits: initiateCompanyUnitsRequest,
    updateCompanyUnits: updateCompanyUnitsRequest,
    clearCompanyUnits: clearCompanyUnitsRequest,
  }, dispatch);
}

CompanyUnitsPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  fetchCompanyUnits: PropTypes.func.isRequired,
  updateCompanyUnits: PropTypes.func.isRequired,
  clearCompanyUnits: PropTypes.func.isRequired,
  companyUnits: PropTypes.shape({
    units: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  }),
  searchConfig: PropTypes.shape({
    unitTerm: PropTypes.string.isRequired,
  }),
  paymentPending: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(CompanyUnitsPage));
